import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Alert,
  CircularProgress,
  IconButton,
  Button,
  SelectChangeEvent
} from '@mui/material';
import {
  Search,
  Warning,
  MonitorHeart,
  Favorite,
  FitnessCenter,
  ArrowBack,
  Phone
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getCacheBustedImageUrl } from '../../utils/s3Utils';

const API_URL = process.env.REACT_APP_API_URL;

interface CriticalPatient {
  id: string;
  patient_id: string;
  name: string;
  age: number;
  gender: string;
  phone: string;
  email: string;
  vitals_reading?: string;
  doctor_name?: string;
  emergency_contact_phone?: string;
  profile_image?: string;
  medical_history?: string;
  allergies?: string;
  is_active: boolean;
  created_at: string;
}

export default function CriticalPatientsPage() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState<CriticalPatient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activityFilter, setActivityFilter] = useState<string>('heartrate');

  // Pagination & Search settings
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Handle search debouncing - triggers fetch automatically via dependency
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchCriticalPatients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Clear patients immediately to give visual feedback that data is refreshing
      setPatients([]);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      // 1. Determine optimized endpoint based on role
      // For Admin: /dashboard/admin/critical-patients
      // For Doctor: /dashboard/critical-patients
      let endpoint = user?.role === 'superadmin'
        ? `${API_URL}/dashboard/admin/critical-patients`
        : `${API_URL}/dashboard/critical-patients`;

      // 2. Build parameters (Standard Pagination: skip/limit)
      const params = new URLSearchParams();
      if (activityFilter !== 'all') {
        params.append('activity_status', activityFilter);
      }

      // Add skip and limit
      const skip = paginationModel.page * paginationModel.pageSize;
      const limit = paginationModel.pageSize;
      params.append('skip', skip.toString());
      params.append('limit', limit.toString());

      // Add search if present
      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
      }

      console.log(`📡 Fetching critical patients from: ${endpoint}?${params.toString()}`);

      const response = await fetch(
        `${endpoint}?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch critical patients');
      }

      const data = await response.json();
      
      // Handle response structure (Expected data.total and data.patients_with_data / data.patients)
      const patientList = data.patients_with_data || data.patients || (Array.isArray(data) ? data : []);
      const total = data.total_patients || data.total || (Array.isArray(data) ? data.length : 0);
      
      setRowCount(total);

      const transformed: CriticalPatient[] = patientList.map((item: any, index: number) => {
        // 1. Extract nested vital objects safely with multiple fallbacks (from HealthMonitorPage)
        const p = item.patient || item;
        const vitalsObj = item.latest_vitals || {};
        
        // Normalize vital sub-objects
        const hrData = vitalsObj.heartrate || vitalsObj.heart_rate || vitalsObj.heartRate || item.heart_rate_data || {};
        const bpData = vitalsObj.bp || vitalsObj.blood_pressure || item.blood_pressure_data || {};
        const bmiData = vitalsObj.bmi || item.bmi_status || item.bmi_data || {};
        
        // Match robust date logic from HealthMonitorPage
        const getTimestamp = () => {
          // Priority 1: Current activity filter
          if (activityFilter === 'heartrate' || (activityFilter === 'all' && (hrData.heart_rate || item.heart_rate))) {
            const h = hrData;
            if (h.heart_rate_created_date && h.heart_rate_created_time) return `${h.heart_rate_created_date}T${h.heart_rate_created_time}`;
            if (h.recorded_at) return h.recorded_at;
            if (item.recorded_at && activityFilter === 'heartrate') return item.recorded_at;
          }
          
          if (activityFilter === 'bp' || (activityFilter === 'all' && (bpData.systolic_bp || item.systolic_bp))) {
            const b = bpData;
            if (b.bp_created_date && b.bp_created_time) return `${b.bp_created_date}T${b.bp_created_time}`;
            if (b.recorded_at) return b.recorded_at;
            if (item.recorded_at && activityFilter === 'bp') return item.recorded_at;
          }
          
          if (activityFilter === 'bmi' || (activityFilter === 'all' && (bmiData.bmi || item.bmi))) {
            const m = bmiData;
            if (m.bmi_created_date && m.bmi_created_time) return `${m.bmi_created_date}T${m.bmi_created_time}`;
            if (m.recorded_at) return m.recorded_at;
            if (item.recorded_at && activityFilter === 'bmi') return item.recorded_at;
          }

          // Fallbacks
          return vitalsObj.recorded_at || 
            item.recorded_at || 
            item.created_at || 
            p.created_at || 
            new Date().toISOString();
        };

        const timestamp = getTimestamp();

        let vitalsReading = 'N/A';
        if (activityFilter === 'heartrate') {
          const hr = hrData.heart_rate || item.heart_rate || item.heartrate || vitalsObj.heartrate || item.latest_heart_rate;
          vitalsReading = hr ? `${hr} bpm` : 'N/A';
        } else if (activityFilter === 'bp') {
          const s = bpData.systolic_bp || item.systolic_bp || item.blood_pressure?.systolic || item.latest_systolic;
          const d = bpData.diastolic_bp || item.diastolic_bp || item.blood_pressure?.diastolic || item.latest_diastolic;
          vitalsReading = (s && d) ? `${s}/${d} mmHg` : 'N/A';
        } else if (activityFilter === 'bmi') {
          const b = bmiData.bmi || item.bmi || vitalsObj.bmi_value || item.latest_bmi;
          vitalsReading = b ? `${parseFloat(b).toFixed(1)}` : 'N/A';
        } else if (activityFilter === 'all') {
          // Detect which vital is available/critical for the summary view
          if (hrData.heart_rate || item.heart_rate || item.latest_heart_rate) {
            vitalsReading = `${hrData.heart_rate || item.heart_rate || item.latest_heart_rate} bpm (HR)`;
          } else if (bpData.systolic_bp || item.systolic_bp || item.latest_systolic) {
            const s = bpData.systolic_bp || item.systolic_bp || item.latest_systolic;
            const d = bpData.diastolic_bp || item.diastolic_bp || item.latest_diastolic;
            vitalsReading = `${s}/${d} mmHg (BP)`;
          } else if (bmiData.bmi || item.bmi || item.latest_bmi) {
            vitalsReading = `${parseFloat(bmiData.bmi || item.bmi || item.latest_bmi).toFixed(1)} (BMI)`;
          } else {
            vitalsReading = 'Multiple/Critical';
          }
        }

        return {
          id: item.id || item._id || `${p.id || p._id || 'patient'}_${index}`,
          patient_id: p.patient_id || '',
          name: p.name || 'Unknown',
          age: p.age || 0,
          gender: p.gender || '',
          phone: p.phone || '',
          email: p.email || '',
          vitals_reading: vitalsReading,
          doctor_name: p.doctor_name || '',
          emergency_contact_phone: p.emergency_contact_phone || '',
          profile_image: p.profile_image || undefined,
          medical_history: p.medical_history || '',
          allergies: p.allergies || '',
          is_active: p.is_active !== undefined ? p.is_active : true,
          created_at: timestamp,
        };
      });

      setPatients(transformed);
    } catch (err: any) {
      console.error('Error fetching critical patients:', err);
      setError(err.message || 'Failed to load critical patients');
    } finally {
      setLoading(false);
    }
  }, [activityFilter, user?.role, paginationModel.page, paginationModel.pageSize, debouncedSearchTerm]);

  useEffect(() => {
    if (authLoading || !user) return;
    fetchCriticalPatients();
  }, [user?.id, authLoading, fetchCriticalPatients, paginationModel.page, paginationModel.pageSize, activityFilter, debouncedSearchTerm]);

  const handleFilterChange = (event: SelectChangeEvent) => {
    setActivityFilter(event.target.value);
    // Reset page to 0 immediately when changing categories
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const displayPatients = useMemo(() => {
    // 1. Apply local filtering for "Instant Search" feedback while typing.
    let filtered = patients;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = patients.filter(p => 
        p.name.toLowerCase().includes(term) ||
        p.email.toLowerCase().includes(term) ||
        (p.phone && p.phone.includes(term))
      );
    }

    return filtered;
  }, [patients, searchTerm]);

  const getFilterLabel = (filter: string) => {
    switch (filter) {
      case 'all': return 'Vital Summary';
      case 'heartrate': return 'Heart Rate';
      case 'bp': return 'Blood Pressure';
      case 'bmi': return 'BMI';
      default: return filter;
    }
  };

  const getFilterIcon = (filter: string) => {
    switch (filter) {
      case 'heartrate': return <MonitorHeart />;
      case 'bp': return <Favorite />;
      case 'bmi': return <FitnessCenter />;
      default: return <Warning />;
    }
  };

  const getFilterColor = (filter: string) => {
    switch (filter) {
      case 'all': return '#7e57c2';
      case 'heartrate': return '#ff6f00';
      case 'bp': return '#ff9800';
      case 'bmi': return '#4caf50';
      default: return '#f44336';
    }
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'name',
      headerName: 'Patient Name',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            src={getCacheBustedImageUrl(params.row.profile_image)}
            sx={{
              width: 32,
              height: 32,
              bgcolor: params.row.profile_image ? 'transparent' : 'primary.main'
            }}
          >
            {!params.row.profile_image && (params.value?.charAt(0) || 'U')}
          </Avatar>
          <Typography variant="body2">{params.value || 'Unknown'}</Typography>
        </Box>
      )
    },
    {
      field: 'age',
      headerName: 'Age',
      width: 80
    },
    {
      field: 'gender',
      headerName: 'Gender',
      width: 100,
      valueFormatter: (value: any) => {
        if (!value) return '';
        return String(value).charAt(0).toUpperCase() + String(value).slice(1);
      }
    },
    {
      field: 'vitals_reading',
      headerName: getFilterLabel(activityFilter),
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#d84315' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 200,
      renderCell: () => (
        <Chip
          label="Needs Priority Review"
          size="small"
          icon={<Warning />}
          sx={{
            bgcolor: '#ffccbc',
            color: '#d84315',
            fontWeight: 'bold',
            '& .MuiChip-icon': {
              color: '#d84315'
            }
          }}
        />
      )
    },
    {
      field: 'emergency_contact_phone',
      headerName: 'Emergency Contact',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Phone sx={{ fontSize: '1rem', color: 'error.main' }} />
          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'bold' }}>
            {params.value || 'N/A'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'created_at',
      headerName: 'Inserted Date',
      width: 180,
      renderCell: (params) => {
        try {
          const val = params.value;
          if (!val) return 'N/A';
          return new Date(val).toLocaleString([], {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        } catch {
          return 'N/A';
        }
      }
    }
  ], [activityFilter]);

  if (authLoading || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(-1)} color="primary">
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          Needs Priority Review
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}



      {/* Filters (Search & Activity) */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: 2,
            alignItems: 'center'
          }}>
            <TextField
              placeholder="Search patients by name..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                // Reset page to 0 when starting a new search
                setPaginationModel(prev => ({ ...prev, page: 0 }));
              }}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Activity Type</InputLabel>
              <Select
                value={activityFilter}
                label="Activity Type"
                onChange={handleFilterChange}
              >
                <MenuItem value="heartrate">Heart Rate </MenuItem>
                <MenuItem value="bp">Blood Pressure </MenuItem>
                <MenuItem value="bmi">BMI Status </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Alert showing total count */}
      {rowCount > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>{rowCount}</strong> patient(s) flagged with critical {getFilterLabel(activityFilter)} readings that need priority review
        </Alert>
      )}

      {/* Data Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Patient Status Summary
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={displayPatients}
              columns={columns}
              paginationMode="server"
              rowCount={rowCount}
              paginationModel={paginationModel}
              onPaginationModelChange={(newModel) => setPaginationModel(newModel)}
              pageSizeOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                border: 'none',
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#e3f2fd'
                },
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center'
                }
              }}
              localeText={{
                noRowsLabel: `No critical patients found for ${getFilterLabel(activityFilter)}`
              }}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
